import * as R from 'ramda'
import useMenu from './useMenu'
import { useStorage } from '@vueuse/core'
const TabKey = '__TAB_INFO__'
const ActiveTabKey = '__ACTIVE_TAB_INFO__'

type TabDetail = {
  value: any
  config: any
  customLabel?: string
  formData?: any
}
type TabsList = Record<string, Record<string, TabDetail>>

const createDictFromObject = R.pipe(
  R.toPairs,
  R.map(
    ([key, value]): Dict<any> => ({
      label: key,
      customLabel: value?.customLabel,
      value,
    }),
  ),
)
const tabs = useStorage<TabsList>(TabKey, {}, window.sessionStorage)
const { activeMenuName } = useMenu()

const activeTabName = useStorage(ActiveTabKey, '', window.sessionStorage)
const currentTabs = computed<Dict<TabDetail>[]>(() => {
  return createDictFromObject(R.prop(activeMenuName.value, tabs.value))
})

const currentIndex = computed(() => {
  return new Date().getTime().toString()
})

// 新增tab
const currentNewIndex = computed(() => {
  return new Date().getTime().toString()
})

const removeAll = () => {
  activeTabName.value = ''
  tabs.value = {}
}

const set = ({
  key,
  customLabel,
  value = {},
  config = {},
}: {
  key?: string
  customLabel?: string
  value: Partial<any>
  config: any
}) => {
  const timestamp = new Date().getTime().toString()
  const label =
    key ||
    (value?.manifestRecord?.billNo
      ? `${value.manifestRecord.billNo}`
      : timestamp)

  activeTabName.value = label
  tabs.value = R.pipe(
    R.set(R.lensPath([activeMenuName.value, label, 'value']), { ...value }),
    R.set(R.lensPath([activeMenuName.value, label, 'config']), { ...config }),
    R.set(
      R.lensPath([activeMenuName.value, label, 'customLabel']),
      customLabel,
    ),
  )(tabs.value)

  // 最多保留5个tab
  tabs.value[activeMenuName.value] = Object.fromEntries(
    Object.entries(tabs.value[activeMenuName.value]).slice(-5),
  )
}
const get = (
  key = activeTabName.value,
  defaultData?: Record<string, any>,
): TabDetail => {
  activeTabName.value = key
  const data = R.path([activeMenuName.value, key], tabs.value)
  if (Object.keys(data.value || {}).length === 0) {
    Object.assign(data.value, defaultData)
  }
  return data
}
const beforeRemoves = reactive<
  Record<string, (type?: string) => Promise<any> | null>
>({})
const remove = async ({
  key = activeTabName.value,
  type,
  callback,
}: {
  key?: string
  type?: string
  callback?: () => any
} = {}) => {
  if (beforeRemoves[key]) {
    await beforeRemoves[key](type)
  }
  tabs.value[activeMenuName.value] = R.omit(
    [key],
    tabs.value[activeMenuName.value],
  )
  activeTabName.value = activeMenuName.value
  callback && callback()
}

const has = (key = '') => {
  if (!key) return false
  if (!tabs.value[activeMenuName.value]) return false
  // return Object.hasOwn(tabs.value[activeMenuName.value], key)
  // 线上客户反馈打开详情页不填充数据，排除发现 360 极速浏览器最新版内核 80.x.x不支持Object.hasOwn，使用下面方法解决
  return Object.prototype.hasOwnProperty.call(
    tabs.value[activeMenuName.value] || {},
    key,
  )
}

// 异步回调按原菜单读取最新缓存，不切换当前标签。
const getMenuTabs = (menuName = activeMenuName.value) => {
  return tabs.value[menuName] || {}
}

const clear = () => {
  tabs.value = {}
}
export default () => {
  return {
    /** 设置当前页面 tab 关闭前的回调，返回值应为Promise: resolve关闭，reject不关闭，清空该方法走默认操作 */
    beforeRemove: toRef(beforeRemoves, activeTabName.value),
    activeMenuName,
    activeTabName,
    currentTabs,
    currentIndex,
    currentNewIndex,
    removeAll,
    set,
    get,
    getMenuTabs,
    remove,
    has,
    clear,
  }
}
